import { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material'
import {
  HomeOutlined,
  AssignmentOutlined,
  CalendarMonthOutlined,
  InventoryOutlined,
  ArchiveOutlined,
  SettingsOutlined,
  ExitToApp,
  EmojiEvents,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/useAuthStore'
import { useFamilyStore } from '@/store/useFamilyStore'
import UserSwitcher from '@/components/family/UserSwitcher'
import NotificationCenter from '@/components/notifications/NotificationCenter'

export default function TopBar() {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [userSwitcherOpen, setUserSwitcherOpen] = useState(false)

  const logout = useAuthStore((state) => state.logout)
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const members = useFamilyStore((state) => state.members)

  const currentUser = members.find((m) => m.id === currentUserId)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleLogout = () => {
    logout()
    handleMenuClose()
  }

  const menuItems = [
    { label: 'Dashboard', icon: <HomeOutlined />, path: '/' },
    { label: 'Tasks', icon: <AssignmentOutlined />, path: '/tasks' },
    { label: 'Calendar', icon: <CalendarMonthOutlined />, path: '/calendar' },
    { label: 'Inventory', icon: <InventoryOutlined />, path: '/inventory' },
    { label: 'Achievements', icon: <EmojiEvents />, path: '/gamification' },
    { label: 'Archive', icon: <ArchiveOutlined />, path: '/archive' },
    { label: 'Settings', icon: <SettingsOutlined />, path: '/settings' },
  ]

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ py: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 0,
              mr: 4,
              fontWeight: 700,
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: '1.5rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
              }}
            >
              🏠
            </Box>
            Happy House
          </Typography>

          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5 }}>
              {menuItems.map((item) => (
                <IconButton
                  key={item.path}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  size="medium"
                  sx={{
                    borderRadius: 3,
                    px: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.15)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Notification Center */}
          {currentUserId && <NotificationCenter />}

          {currentUser && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                px: 1.5,
                py: 1,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
              onClick={() => setUserSwitcherOpen(true)}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: currentUser.color,
                  fontSize: '0.875rem',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
                src={currentUser.avatar_url || undefined}
              >
                {currentUser.name[0]}
              </Avatar>
              {!isMobile && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}
                >
                  {currentUser.name}
                </Typography>
              )}
            </Box>
          )}

          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            size="medium"
            sx={{
              ml: 1.5,
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.15)',
                transform: 'rotate(45deg)',
              },
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <SettingsOutlined />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            sx={{
              '& .MuiPaper-root': {
                minWidth: 200,
              },
            }}
          >
            <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }}>
              <SettingsOutlined sx={{ mr: 1.5 }} fontSize="small" />
              Settings
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1.5 }} fontSize="small" />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <UserSwitcher
        open={userSwitcherOpen}
        onClose={() => setUserSwitcherOpen(false)}
      />
    </>
  )
}
